/**
 * Recall Master API Client
 *
 * Wraps all endpoints from the tms-api-recall-master service (DynamoDB-backed).
 *
 * Follows the same gateway + service-prefix pattern as products.api.ts:
 *   - Uses getApiClient() (shared gateway client)
 *   - Routes prefixed with API_ROUTES.RECALL_MASTER (= /recallMaster/api/v1)
 *   - createApiClient from @tms-phoenix/ui-vue auto-unwraps the ApiResponse envelope
 */
import type { AxiosResponse } from 'axios';
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import type {
  ProductRecallDynamoItem,
  ProductRecallDynamoListResponse,
  ProductRecallDynamoListQuery,
  CreateProductRecallDynamo,
  UpdateProductRecallDynamo,
} from '@tms-phoenix/tms-types';

const API_PREFIX = API_ROUTES.RECALL_MASTER;
const api = getApiClient();

// ---------------------------------------------------------------------------
// List recalls
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/productRecalls
 *
 * Cursor-paginated list with optional GSI-backed filters.
 */
export function fetchRecalls(
  query: Partial<ProductRecallDynamoListQuery> = {},
): Promise<AxiosResponse<ProductRecallDynamoListResponse>> {
  return api.get<ProductRecallDynamoListResponse>(
    `${API_PREFIX}/productRecalls`,
    { params: query },
  );
}

// ---------------------------------------------------------------------------
// Get single recall
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/productRecalls/:id
 */
export function fetchRecallById(
  id: string,
): Promise<AxiosResponse<ProductRecallDynamoItem>> {
  return api.get<ProductRecallDynamoItem>(
    `${API_PREFIX}/productRecalls/${id}`,
  );
}

// ---------------------------------------------------------------------------
// Create recall
// ---------------------------------------------------------------------------

/**
 * POST /api/v1/productRecalls
 *
 * Returns 201 with the created recall including server-generated recall_id
 * and recall_reference.
 */
export function createRecall(
  body: CreateProductRecallDynamo,
): Promise<AxiosResponse<ProductRecallDynamoItem>> {
  return api.post<ProductRecallDynamoItem>(
    `${API_PREFIX}/productRecalls`,
    body,
  );
}

// ---------------------------------------------------------------------------
// Update recall (partial)
// ---------------------------------------------------------------------------

/**
 * PATCH /api/v1/productRecalls/:id
 */
export function updateRecall(
  id: string,
  body: UpdateProductRecallDynamo,
): Promise<AxiosResponse<ProductRecallDynamoItem>> {
  return api.patch<ProductRecallDynamoItem>(
    `${API_PREFIX}/productRecalls/${id}`,
    body,
  );
}

// ---------------------------------------------------------------------------
// Soft-delete recall
// ---------------------------------------------------------------------------

/**
 * DELETE /api/v1/productRecalls/:id
 *
 * Soft-deletes the recall (sets is_deleted = true, status = CLOSED).
 */
export function deleteRecall(
  id: string,
  reason: string,
): Promise<AxiosResponse<{ recall_id: string; deleted: boolean }>> {
  return api.delete<{ recall_id: string; deleted: boolean }>(
    `${API_PREFIX}/productRecalls/${id}`,
    { data: { reason } },
  );
}
